import EmptyView from 'general/components/Others/EmptyView';
import AppColors from 'general/constants/AppColors';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useCallback } from 'react';
import DataTable from 'react-data-table-component';
import { Table } from 'antd';
import tableStyle from './tableStyle';
import tableStyleAction from './tableStyleAction';
import AppConfigs from 'general/constants/AppConfigs';

KTTable.propTypes = {
  idType: PropTypes.string,
  columns: PropTypes.array,
  items: PropTypes.array,
  disabledItems: PropTypes.array,
  preselectedItems: PropTypes.array,
  selectableRows: PropTypes.bool,
  striped: PropTypes.bool,
  compactGrid: PropTypes.bool,
  loading: PropTypes.bool,
  onClickedRow: PropTypes.func,
  onDoubleClickedRow: PropTypes.func,
  onSelectedRowsChanged: PropTypes.func,
  noDataComponent: PropTypes.element,
  scrollable: PropTypes.bool,
  showBorderBottom: PropTypes.bool,
  hasActionColumn: PropTypes.bool,
  groupHeader: PropTypes.bool,
  selectableRowsNoSelectAll: PropTypes.bool,
};

function KTTable({
  idType = 'id',
  columns = [],
  items = [],
  disabledItems = [],
  preselectedItems = [],
  selectableRows = true,
  striped = true,
  compactGrid = false,
  loading = false,
  onClickedRow,
  onDoubleClickedRow,
  onSelectedRowsChanged,
  noDataComponent,
  scrollable = false,
  showBorderBottom = false,
  hasActionColumn = true,
  groupHeader = false,
  selectableRowsNoSelectAll = false,
}) {
  const isHideBike = AppConfigs.env.hideBike == 1;

  const filteredColumns = isHideBike
    ? columns.filter(col => col.id !== 'VEHICLE_TYPE')
    : columns;

  useEffect(() => {
    if (!groupHeader) {
      const dataTable = document.querySelector('.rdt_Table');

      if (dataTable) {
        const preventDrag = (event) => {
          event.preventDefault();
        };

        dataTable.addEventListener('dragstart', preventDrag);

        return () => {
          dataTable.removeEventListener('dragstart', preventDrag);
        };
      }
    }
  }, [groupHeader]);

  const disabledIds = useMemo(() => {
    return new Set(
      disabledItems?.map((item) => item[idType]) || []
    );
  }, [disabledItems, idType]);

  const preselectedIds = useMemo(() => {
    return new Set(
      preselectedItems?.map((item) => item[idType]) || []
    );
  }, [preselectedItems, idType]);

  const rowDisabledCriteria = useCallback(
    (row) => {
      return disabledIds.has(row[idType]);
    },
    [disabledIds, idType]
  );

  const rowPreselectedCriteria = useCallback(
    (row) => {
      return preselectedIds.has(row[idType]);
    },
    [preselectedIds, idType]
  );

  const handleSelectedRowsChanged = useCallback(
    ({ selectedRows }) => {
      if (onSelectedRowsChanged) {
        onSelectedRowsChanged(selectedRows);
      }
    },
    [onSelectedRowsChanged]
  );

  const antRowSelection = selectableRows
    ? {
      selectedRowKeys:
        preselectedItems?.map((item) => item[idType]) || [],

      getCheckboxProps: (record) => ({
        disabled: disabledIds.has(record[idType]),
      }),

      onChange: (selectedRowKeys, selectedRows) => {
        if (onSelectedRowsChanged) {
          onSelectedRowsChanged(selectedRows);
        }
      },
    }
    : null;

  if (groupHeader) {
    return (
      <div
        style={{
          borderRadius: '5px',
          overflow: scrollable ? 'auto' : 'hidden',
        }}
        className={items.length === 0 ? 'no-data-table' : ''}
      >
        <Table
          className={
            items.length === 0
              ? 'no-data-table'
              : 'kt-table-bordered'
          }
          columns={filteredColumns}
          dataSource={items}
          rowSelection={antRowSelection}
          loading={loading}
          locale={{
            emptyText: noDataComponent ?? <EmptyView />,
          }}
          pagination={false}
          scroll={
            scrollable
              ? { x: 'max-content' }
              : undefined
          }
          onRow={(record) => ({
            onClick: () => onClickedRow?.(record),
            onDoubleClick: () =>
              onDoubleClickedRow?.(record),
          })}
          rowKey={idType}
          bordered={showBorderBottom}
          showHeader={items.length > 0}
        />
      </div>
    );
  }

  return (
    <div
      className={`overflow-hidden ${selectableRowsNoSelectAll
        ? 'hide-select-all-checkbox'
        : ''
        }`}
      style={{
        borderLeft:
          items.length > 0
            ? `1px solid ${AppColors.gray[300]}`
            : '',
        borderRight:
          items.length > 0
            ? `1px solid ${AppColors.gray[300]}`
            : '',
        borderRadius: '5px',
      }}
    >
      <DataTable
        keyField={idType}
        columns={filteredColumns}
        data={items}
        customStyles={
          hasActionColumn
            ? tableStyleAction
            : tableStyle
        }
        responsive
        noHeader
        selectableRows={selectableRows}
        striped={striped}
        dense={compactGrid}
        progressPending={loading}
        selectableRowDisabled={rowDisabledCriteria}
        selectableRowSelected={rowPreselectedCriteria}
        noDataComponent={
          noDataComponent ?? <EmptyView />
        }
        onSelectedRowsChange={
          handleSelectedRowsChanged
        }
        onRowClicked={onClickedRow}
        onRowDoubleClicked={onDoubleClickedRow}
        pointerOnHover
        highlightOnHover={false}
        selectableRowsHighlight
      />
    </div>
  );
}

export default KTTable;